// Utility functions test suite

/**
 * Simple utility functions for testing purposes
 */

export const formatCurrency = (amount) => {
  return `$${parseFloat(amount).toFixed(2)}`;
};

export const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const capitalize = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const sum = (...numbers) => {
  return numbers.reduce((total, num) => total + num, 0);
};

/**
 * Format a Date as YYYY-MM-DD in the *local* timezone.
 *
 * Not toISOString().slice(0,10) — that converts to UTC first, so anywhere east
 * of Greenwich a date picked late in the evening comes out as the previous day.
 * That is what stamped invoices with yesterday's issue date.
 */
export const toDateInputValue = (date) => {
  const d = date ? new Date(date) : new Date();
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * How many days after the issue date a payment term falls due.
 *
 * Terms reach us from two places that were never wired together: Quotation
 * Settings offers a hardcoded list of strings, while the day counts live in a
 * hand-maintained `payment_terms` table. When a settings string doesn't match a
 * row exactly the old code silently assumed 30 days, so "Due on Receipt" came
 * out a month late. Matching is therefore case/whitespace tolerant, and known
 * term shapes are understood even when the table has no row for them.
 *
 * @returns {number|null} days, or null when the term is genuinely unknown —
 *   callers must leave the existing due date alone rather than guess. Note 0 is
 *   a valid answer (due immediately) and must not be treated as "not found".
 */
export const resolvePaymentTermDays = (termName, termsList) => {
  const name = String(termName || "").trim();
  if (name === "") return null;

  const list = Array.isArray(termsList) ? termsList : [];
  const usable = (t) => t && t.no_of_days !== null && t.no_of_days !== undefined;

  const exact = list.find((t) => usable(t) && t.term_name === name);
  if (exact) return Number(exact.no_of_days);

  const lower = name.toLowerCase();
  const loose = list.find(
    (t) => usable(t) && String(t.term_name).trim().toLowerCase() === lower
  );
  if (loose) return Number(loose.no_of_days);

  // Fallbacks by term shape, so a missing or misspelt row still resolves.
  if (/^(due (on|upon) receipt|cod|cia)$/.test(lower)) return 0;
  if (/^cash (on delivery|in advance)\b/.test(lower)) return 0;

  // "Net 30", and the discount forms such as "2/10 Net 30" — the net number is
  // the due date; the leading fraction is only an early-payment discount.
  const net = /(?:^|\s)net\s*(\d+)\s*$/.exec(lower);
  if (net) return Number(net[1]);

  if (/^end of month\b|^eom$/.test(lower)) return "EOM";

  return null;
};

/**
 * Apply a resolved term to an issue date. Kept beside the resolver so every
 * caller advances the date the same way, including the end-of-month case.
 */
export const dueDateFromTerm = (issueDate, days) => {
  if (days === null || days === undefined) return null;
  const base = issueDate ? new Date(issueDate) : new Date();
  if (isNaN(base.getTime())) return null;
  if (days === "EOM") {
    return new Date(base.getFullYear(), base.getMonth() + 1, 0);
  }
  base.setDate(base.getDate() + Number(days));
  return base;
};
